import React, { useEffect, useRef } from "react";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { FilterInfo } from "@/components/atomic/voc-filter/FilterInfo";
import { Message, MessageType } from "@/models/conversation";
import { AssistantMessage } from "./AssistantMessage";
import { UserMessage } from "./UserMessage";

interface ChatMessageListProps {
  messages: Message[];
  isChatLoading?: boolean;
  onViewVocs?: (messageId: string) => void;
  messageRefs?: React.MutableRefObject<Record<string, HTMLDivElement>>;
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  isChatLoading,
  onViewVocs,
  messageRefs,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const messagesLengthRef = useRef(messages.length);

  // モーダルが開いた直後の最初のメッセージ更新だけは instant（auto）でスクロール
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    // 初期メッセージがあるので、1件以下という条件にしておく
    const shouldInstant = messagesLengthRef.current <= 1
      || messages.length === messagesLengthRef.current;

    el.scrollTo({
      top: el.scrollHeight,
      behavior: shouldInstant ? "instant" : "smooth",
    });

    messagesLengthRef.current = messages.length;
  }, [messages.length]);

  let lastCategories: string[] | undefined;
  let lastVocPeriodStart: Date | undefined;
  let lastVocPeriodEnd: Date | undefined;
  let lastAttributeFilters: Message["attributeFilters"];
  let lastTagIds: string[] | undefined;
  let isFirstUserMessage = true;

  return (
    <div className="flex h-full w-full justify-center">
      <ScrollArea className="h-full w-full max-w-[788px] m-8 pr-[14px]" viewportRef={viewportRef}>
        <div className="flex flex-col gap-4 pb-[50px]">
          {messages.map((message) => {
            if (message.type === MessageType.USER) {
              const categoriesChanged = (lastCategories?.toString() ?? "") !== (message.categories?.toString() ?? "");
              const periodChanged = lastVocPeriodStart?.getTime() !== message.vocPeriodStart?.getTime()
                || lastVocPeriodEnd?.getTime() !== message.vocPeriodEnd?.getTime();
              const attributeFiltersChanged =
                JSON.stringify(lastAttributeFilters) !== JSON.stringify(message.attributeFilters);
              const tagIdsChanged = (lastTagIds || []).toSorted().toString()
                !== (message.tagIds || []).toSorted().toString();

              // 初回または変更があった場合のみ FilterInfo を表示
              const shouldShowInfo = isFirstUserMessage || categoriesChanged || periodChanged
                || attributeFiltersChanged || tagIdsChanged;

              // 初回フラグをfalseに更新
              if (isFirstUserMessage) {
                isFirstUserMessage = false;
              }

              // 最新の状態を保存
              lastCategories = message.categories;
              lastVocPeriodStart = message.vocPeriodStart;
              lastVocPeriodEnd = message.vocPeriodEnd;
              lastAttributeFilters = message.attributeFilters;
              lastTagIds = message.tagIds;

              return (
                <React.Fragment key={message.id}>
                  {shouldShowInfo && (
                    <FilterInfo
                      categories={message.categories}
                      vocPeriodStart={message.vocPeriodStart}
                      vocPeriodEnd={message.vocPeriodEnd}
                      attributeFilters={message.attributeFilters}
                      tagIds={message.tagIds}
                    />
                  )}
                  <UserMessage
                    message={message}
                  />
                </React.Fragment>
              );
            }

            return (
              <AssistantMessage
                key={message.id}
                message={message}
                onViewVocs={() => onViewVocs?.(message.id)}
                messageRefs={messageRefs}
              />
            );
          })}
          {isChatLoading && (
            <AssistantMessage
              isLoading={true}
            />
          )}
        </div>
        <ScrollBar />
      </ScrollArea>
    </div>
  );
};
